import { Fragment, useEffect, useMemo, useState } from 'react';

import {
  SearchableVendorTaxonomyKey,
  VendorTaxonomyKey,
  VENUES_TAXONOMY_KEY,
} from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { addDays, isBeforeDate } from '@zola-helpers/client/dist/es/util/dateUtils';
import getEnvironment, { EnvironmentTypes } from '@zola-helpers/client/dist/es/util/environment';
import { useEffectOnce } from '@zola/zola-ui/src/hooks';

import _get from 'lodash/get';

import { loadAccountPreferences } from '~/actions/couples/accountPreferencesActions';
import { loadMiniQuizAnswers } from '~/actions/couples/miniQuizActions';
import { setUserSetting } from '~/actions/couples/userSettingsActions';
import { useModal } from '~/contexts/ModalContext';
import { useUserContext } from '~/contexts/UserContext';
import { useAnyModalShown } from '~/hooks/useAnyModalShown';
import { getMarketplaceSessionStartTime } from '~/hooks/useMarketplaceSessionCookie';
import { usePageNotClicked } from '~/hooks/usePageNotClicked';
import useUserSettings from '~/hooks/useUserSettings';
import {
  getMiniQuizPromptTimeoutUserSetting,
  getMiniQuizUserSettingForTaxonomy,
} from '~/pages/couples/explore/components/MiniQuiz/miniQuizUserSettings';
import { useSearchResultsPageContext } from '~/pages/couples/explore/components/SearchResults/contexts/SearchResultsPageContext';
import { useAppDispatch, useAppSelector } from '~/reducers';
import { getVendorOutreachFromWeddingDetailsPreference } from '~/selectors/couples/accountPreferencesSelectors';
import { getMiniQuizForTaxonomy } from '~/selectors/couples/miniQuizSelectors';
import { isAutomatedTest } from '~/util/automatedTestUtils';
import { addMinutes, parseDateUtc } from '~/util/dateUtils';

import { getMiniQuiz } from '../MiniQuizModal/getMiniQuiz';
import { useBookedVendorsAllowMiniQuiz } from '../MiniQuizModal/hooks/useBookedVendorsAllowMiniQuiz';
import { addSuccessToastToSessionStorageAndReload } from '../MiniQuizModal/showMiniQuizModal';
import type { MiniQuizPromptModalProps } from './MiniQuizPromptModal';

/**
 * Returns a fixed point in time that, if the current marketplace session was
 * started before, then we can show the modal.  We do not want to show the modal
 * unless the session has been active for "1 minute" so that we don't pop the
 * modal right when the couple logs in, or starts engaging with marketplace
 */
const getSessionStartTimeRequirement = () => {
  // Note: set this to zero, I didn't want to lose the session option (we used
  // it previously for a modal that shows without user interaction) but we have
  // a 15 second timeout for showing the modal, which should be good enough
  return addMinutes(new Date(), 0);
};

/**
 * Returns a fixed point in time that, if the last time the modal was displayed
 * is before, then we can show the modal.  We do not want to show the modal more
 * than "once a day".
 */
const getModalLastSeenRequirement = () => {
  if (getEnvironment() !== EnvironmentTypes.PRODUCTION) {
    return addMinutes(new Date(), -30);
  }
  return addDays(new Date(), -1);
};

/**
 * Determine if certain preconditions (time based, or the user doing something
 * like not-clicking, etc) have been satisfied in a way that allows the
 * prompt to show.
 *
 * - Wait 15 seconds - thats the business's rule of not showing this thing not showing too fast.
 * - Make sure no other modals have shown
 * - Is the marketplace session old enough? (have they been in web-marketplace for a minimum period of time)
 *
 * Optionally later:
 * - Prevent the mini-quiz from showing if the user clicked somewhere on the page (like an account menu, navigation, or search filter)
 */
const usePromptPreconditions = (): boolean => {
  // ----------------------------------------------------------------------------
  //   Nicole Merritt
  //   great, getting feedback from the biz on the time on page - will keep you posted
  //
  //   Nicole Merritt
  //   thinking 20 seconds after page load
  //
  //   John Naegle
  //   thank you!
  //
  //   Nicole Merritt
  //   let's start with 20 seconds
  // ----------------------------------------------------------------------------
  //   Nicole Merritt
  //   i just took a look - could we try 15 seconds and see how it feels?
  // ----------------------------------------------------------------------------
  //
  const [delayElapsed, setDelayElapsed] = useState(false);
  useEffect(() => {
    const timeout = setTimeout(() => setDelayElapsed(true), 15 * 1000);
    return () => clearTimeout(timeout);
  }, []);

  // We don't want this modal to interfere with any other.
  const anyModalShown = useAnyModalShown();

  return useMemo(() => {
    const sessionStartTime = getMarketplaceSessionStartTime();

    // The session started long enough ago and this particular page has
    // been loaded for the desired time
    if (
      delayElapsed &&
      sessionStartTime &&
      isBeforeDate(sessionStartTime, getSessionStartTimeRequirement())
    ) {
      return Boolean(!anyModalShown && !isAutomatedTest());
    }
    return false;
  }, [anyModalShown, delayElapsed]);
};

/**
 * Based on the user opted-in. or opting-out, or not yet making a MTFM selection
 * for wedding details, return if their selections allow us to show the modal on
 * the SRP.
 *
 * The mini-quiz is allowed (based only on preferences) when:
 * - They have opt-in
 * OR
 * - They have not made a selection
 * OR
 * - They have opted-out, and are now eligible for
 * @returns
 */
const useAccountPreferencesAllowsMiniQuiz = (): boolean => {
  const dispatch = useAppDispatch();

  // Delay making more API calls for a second, no need to make more API calls
  // on load than necessary
  useEffect(() => {
    setTimeout(() => {
      dispatch(loadAccountPreferences()).catch(() => null);
    }, 1000);
  }, [dispatch]);

  const { enabled, rePromptEligible, madeSelection } = useAppSelector(
    getVendorOutreachFromWeddingDetailsPreference
  );

  // They've never made a selection
  // Or they opted in
  // Or they opted out, but they can be re-prompted now
  return !madeSelection || enabled || rePromptEligible;
};

const useLocationHasNotChanged = (): boolean => {
  const [firstLocation, setFirstLocation] = useState<string>();
  const pathAndSearch = `${window.location.pathname}${window.location.search}`;

  useEffectOnce(() => {
    setFirstLocation(pathAndSearch);
  });

  return pathAndSearch === firstLocation;
};

/**
 * Determine if user-settings related to the mini-quiz allow the prompt.  There
 * are two settings:
 * - 1) Have they taken the quiz
 * - 2) Have they recently seen the modal.
 *
 * - If they have taken the quiz, we won't show the prompt
 * - If they have recently seen the modal, we won't show the prompt
 */
const useUserSettingsAllowsPrompt = ({
  taxonomyKey,
}: {
  taxonomyKey: VendorTaxonomyKey;
}): boolean => {
  const miniQuizPromptTimeoutUserSetting = getMiniQuizPromptTimeoutUserSetting();

  const userSettingKeyForTaxonomy = useMemo(
    () => getMiniQuizUserSettingForTaxonomy(taxonomyKey),
    [taxonomyKey]
  );

  // This has to be a memo, or we make a boatload of API requests
  const keys = useMemo(() => {
    return [userSettingKeyForTaxonomy, miniQuizPromptTimeoutUserSetting];
  }, [miniQuizPromptTimeoutUserSetting, userSettingKeyForTaxonomy]);
  const { settings: userSettings, loaded } = useUserSettings(keys);

  return useMemo(() => {
    if (!loaded) return false;

    // Oh, they already took the quiz, so nevermind all that other stuff
    if (userSettings[userSettingKeyForTaxonomy].flag) {
      return false;
    }

    const lastTimeModalShownSetting = userSettings[miniQuizPromptTimeoutUserSetting];

    if (lastTimeModalShownSetting.flag && lastTimeModalShownSetting.updatedAt) {
      // They've seen this modal before, make sure it hasn't been shown too recently
      const timeModalLastShown = parseDateUtc(lastTimeModalShownSetting.updatedAt);
      return isBeforeDate(timeModalLastShown, getModalLastSeenRequirement());
    }

    // The last time shown flag wasn't set, that means this user has never seen this modal
    return true;
  }, [loaded, miniQuizPromptTimeoutUserSetting, userSettingKeyForTaxonomy, userSettings]);
};

const useWeddingDetailsAllowsQuiz = ({
  taxonomyKey,
}: {
  taxonomyKey: SearchableVendorTaxonomyKey;
}): boolean => {
  const quiz = useMemo(() => (taxonomyKey ? getMiniQuiz(taxonomyKey) : undefined), [taxonomyKey]);

  const { loaded, values } = useAppSelector(getMiniQuizForTaxonomy(taxonomyKey));

  const someQuestionNotAnswered = useMemo(() => {
    if (quiz) {
      return quiz.some((question) => !question.isAnswered(values));
    }
    return true;
  }, [quiz, values]);

  if (!loaded) {
    return false;
  }

  return someQuestionNotAnswered;
};

/**
 * Shows the mini-quiz prompt.  Here are the rules for showing the mini-quiz
 * prompt:
 *
 * - Do the right thing
 * - Don't do it too often
 *
 * Seriously, show the modal when:
 * - The user is not a guest
 * - No other modals have shown on the screen
 * - The URL of the page hasn't changed since it was loaded
 * - They haven't already taken the quiz in this category
 * - They haven't opted out of MTFM from wedding details in the last 14 days
 * - They haven't clicked on the page in the last 15 seconds
 * - The page has been loaded for at least 15 seconds
 * - They have not yet booked the category
 * - They haven't seen this modal (any category) in the last 24 hours
 *
 */
const useMiniQuizPrompt = ({ taxonomyKey }: { taxonomyKey: SearchableVendorTaxonomyKey }) => {
  const dispatch = useAppDispatch();
  const { isModalVisible, showV2PredefinedModal } = useModal();

  const preconditionsMet = usePromptPreconditions();
  const preferenceAllowsPrompt = useAccountPreferencesAllowsMiniQuiz();
  const userSettingsAllowsPrompt = useUserSettingsAllowsPrompt({ taxonomyKey });
  const locationHasNotChanged = useLocationHasNotChanged();
  const pageNotClicked = usePageNotClicked({ delay: 15000 });

  //

  const { loaded: bookedVendorsLoaded, miniQuizAllowed: bookedVendorsAllowsQuiz } =
    useBookedVendorsAllowMiniQuiz({ taxonomyKey });

  const weddingDetailsAllowsPrompt = useWeddingDetailsAllowsQuiz({ taxonomyKey });

  const canShowModal =
    !isModalVisible &&
    preconditionsMet &&
    preferenceAllowsPrompt &&
    userSettingsAllowsPrompt &&
    locationHasNotChanged &&
    pageNotClicked &&
    bookedVendorsLoaded &&
    bookedVendorsAllowsQuiz &&
    weddingDetailsAllowsPrompt;

  useEffect(() => {
    if (canShowModal) {
      dispatch(setUserSetting(getMiniQuizPromptTimeoutUserSetting())).catch(() => null);
      showV2PredefinedModal<MiniQuizPromptModalProps>(
        'MINI_QUIZ_PROMPT_MODAL',
        {
          dialogHeadingId: 'dialog-heading',
          triggerRef: null,
          lockBgScrolling: true,
        },
        {
          taxonomyKey,
          onQuizSubmitted: addSuccessToastToSessionStorageAndReload,
        }
      );
    }
  }, [canShowModal, dispatch, showV2PredefinedModal, taxonomyKey]);
};

const Prompt = (props: { taxonomyKey: SearchableVendorTaxonomyKey }) => {
  useMiniQuizPrompt(props);

  return <Fragment />;
};

// Component to work around conditional hooks
export const MiniQuizPrompt = () => {
  const userContext = useUserContext();
  const isGuest = _get(userContext, 'is_guest');

  const { vendorSearch } = useSearchResultsPageContext();

  const taxonomyKey = vendorSearch.selectedTaxonomy?.key as SearchableVendorTaxonomyKey | undefined;

  const dispatch = useAppDispatch();
  const guestMiniQuiz = Boolean(isGuest);

  // Load the couple's mini-quiz answers for this vendor category
  useEffect(() => {
    dispatch(
      loadMiniQuizAnswers(
        (vendorSearch?.selectedTaxonomy?.key as SearchableVendorTaxonomyKey) || VENUES_TAXONOMY_KEY,
        guestMiniQuiz
      )
    ).catch(() => null);
  }, [dispatch, vendorSearch?.selectedTaxonomy?.key, guestMiniQuiz]);

  if (taxonomyKey && userContext && !userContext.is_guest) {
    return <Prompt taxonomyKey={taxonomyKey} />;
  }
  return <Fragment />;
};
